/**
 * Token-Ablage für oidc-client-ts, die den Refresh-Token NICHT persistiert (#59).
 *
 * Hintergrund: Tester und Kunden-App teilen sich same-origin den localStorage.
 * Dort lag seit dem Silent-Renew (offline_access) auch der Refresh-Token —
 * ein LANGLEBIGES Credential, das jede XSS-Lücke in irgendeiner eingebetteten
 * App hätte abgreifen können.
 *
 * Hybrid-Ansatz: Access-Token und Profil landen weiterhin im Basis-Store
 * (Reload innerhalb der Access-Token-Laufzeit bleibt nahtlos), der
 * Refresh-Token bleibt nur im Arbeitsspeicher dieses Tabs. Nach einem Reload
 * fehlt er — sobald das Access-Token abläuft, folgt einmalig der SSO-Redirect
 * (Authentik-Session lebt meist noch, also ohne Passwort). Ein XSS-Treffer
 * erbeutet damit höchstens ein Minuten-Token.
 */

import type { StateStore } from "oidc-client-ts"

export class RefreshTokenImSpeicherStore implements StateStore {
  private readonly fluechtig = new Map<string, string>()

  constructor(private readonly basis: StateStore) {}

  async set(key: string, value: string): Promise<void> {
    let persistiert = value
    const daten = parseObjekt(value)
    if (daten && "refresh_token" in daten) {
      const rt = daten.refresh_token
      if (typeof rt === "string" && rt) this.fluechtig.set(key, rt)
      else this.fluechtig.delete(key)
      delete daten.refresh_token
      persistiert = JSON.stringify(daten)
    }
    await this.basis.set(key, persistiert)
  }

  async get(key: string): Promise<string | null> {
    const wert = await this.basis.get(key)
    if (wert == null) return wert
    const rt = this.fluechtig.get(key)
    if (!rt) return wert
    const daten = parseObjekt(wert)
    if (!daten) return wert
    daten.refresh_token = rt
    return JSON.stringify(daten)
  }

  async remove(key: string): Promise<string | null> {
    this.fluechtig.delete(key)
    return this.basis.remove(key)
  }

  getAllKeys(): Promise<string[]> {
    return this.basis.getAllKeys()
  }
}

function parseObjekt(text: string): Record<string, unknown> | null {
  try {
    const o: unknown = JSON.parse(text)
    return o && typeof o === "object" && !Array.isArray(o) ? (o as Record<string, unknown>) : null
  } catch {
    return null
  }
}
